import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock, ChevronDown, ChevronUp, Loader2, Play, Plus, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatInTz, tzAbbreviation } from "@/lib/timezone";

/**
 * "Scheduled research" panel on the Research tab.
 *
 * Lets the editor set recurring runs by day-of-week + wall-clock time —
 * e.g. Mon-Fri at 06:30 so the ideas are already waiting in Past Runs
 * when they sit down at 07:00, plus a separate weekend rule at 08:00.
 *
 * DAY INDEXING: 0 = Monday ... 6 = Sunday, matching Python's
 * `datetime.weekday()` on the backend. This deliberately does NOT use
 * JS `getDay()` (Sun = 0) — the array below is the single source of
 * truth for the mapping, so never reorder it.
 *
 * Times are wall-clock in the PROPERTY's newsroom timezone, never the
 * browser's. An editor travelling to Mexico City still sees (and gets)
 * a 06:30 Tijuana run.
 */
const DAYS = [
  { v: 0, short: "Mon" },
  { v: 1, short: "Tue" },
  { v: 2, short: "Wed" },
  { v: 3, short: "Thu" },
  { v: 4, short: "Fri" },
  { v: 5, short: "Sat" },
  { v: 6, short: "Sun" },
];

const WEEKDAY_PRESET = [0, 1, 2, 3, 4];
const WEEKEND_PRESET = [5, 6];

const EMPTY_DRAFT = {
  name: "",
  days_of_week: WEEKDAY_PRESET,
  time_local: "06:30",
  guidelines: "",
};

function DayPicker({ value, onChange, idPrefix }) {
  const toggle = (d) =>
    onChange(
      value.includes(d) ? value.filter((x) => x !== d) : [...value, d].sort((a, b) => a - b)
    );
  return (
    <div className="flex flex-wrap gap-1">
      {DAYS.map((d) => {
        const on = value.includes(d.v);
        return (
          <button
            key={d.v}
            type="button"
            data-testid={`${idPrefix}-day-${d.v}`}
            aria-pressed={on}
            onClick={() => toggle(d.v)}
            className={
              "px-2.5 py-1 border text-[11px] font-mono-display tracking-wider uppercase " +
              (on
                ? "border-[var(--slate-900)] bg-[var(--slate-900)] text-white"
                : "border-[var(--border)] bg-white text-[var(--ink-muted)] hover:border-[var(--slate-900)]")
            }
          >
            {d.short}
          </button>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  const cfg = {
    ok: { label: "last run ok", cls: "text-emerald-700" },
    running: { label: "running now", cls: "text-amber-700" },
    error: { label: "last run failed", cls: "text-red-700" },
    skipped_busy: { label: "skipped · run in progress", cls: "text-amber-700" },
  }[status];
  if (!cfg) return null;
  return (
    <span className={`text-[11px] font-mono-display ${cfg.cls}`}>
      · {cfg.label}
    </span>
  );
}

export default function ResearchSchedulePanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["research-schedules"],
    queryFn: api.listResearchSchedules,
    // Cheap query, and `next_run_at` / `last_status` move on their own
    // in the background — keep it reasonably fresh while the tab is open.
    refetchInterval: 60_000,
  });
  const schedules = useMemo(() => data?.items || [], [data]);
  const tz = data?.timezone || "America/Tijuana";
  const activeCount = schedules.filter((s) => s.enabled).length;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["research-schedules"] });
    // The unified queue on /scheduled shows these rows too.
    qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
  };

  const createMut = useMutation({
    mutationFn: (payload) => api.createResearchSchedule(payload),
    onSuccess: (s) => {
      invalidate();
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      toast.success(`Schedule saved — ${s.summary}`);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not save schedule"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }) => api.updateResearchSchedule(id, patch),
    onSuccess: () => invalidate(),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not update schedule"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteResearchSchedule(id),
    onSuccess: () => {
      invalidate();
      toast.success("Schedule deleted");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not delete schedule"),
  });

  const runNowMut = useMutation({
    mutationFn: (id) => api.runResearchScheduleNow(id),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["past-runs"] });
      toast.success(
        "Run started — it'll appear in Past Runs in a minute or two."
      );
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not start run"),
  });

  const submitDraft = () => {
    if (!draft.days_of_week.length) {
      toast.error("Pick at least one day");
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(draft.time_local)) {
      toast.error("Time must be HH:MM");
      return;
    }
    createMut.mutate(draft);
  };

  return (
    <div
      data-testid="research-schedule-panel"
      className="bg-white border border-[var(--border)] mb-8"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="research-schedule-toggle"
        className="w-full px-5 py-3 border-b border-[var(--border)] flex items-center gap-2 text-left hover:bg-neutral-50"
      >
        <CalendarClock className="w-4 h-4 text-[var(--accent)]" />
        <span className="overline">Scheduled research</span>
        <span className="text-[11px] font-mono-display text-[var(--ink-muted)]">
          {isLoading
            ? "loading…"
            : activeCount === 0
            ? "none active"
            : `${activeCount} active`}
        </span>
        <span className="ml-auto text-[var(--ink-muted)]">
          {open ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </span>
      </button>

      {open && (
        <div className="px-5 py-4">
          <p className="text-sm text-[var(--ink-muted)] max-w-2xl mb-4">
            Run research automatically on a repeating weekly rhythm, so the
            ideas are waiting for you when you sit down. Times are wall-clock
            in{" "}
            <span className="text-[var(--ink)]">{tz}</span> ({tzAbbreviation(tz)})
            and stay put across daylight-saving changes. Results land in{" "}
            <em>Past Runs</em> like any other run.
          </p>

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)] py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading schedules…
            </div>
          )}

          {!isLoading && schedules.length === 0 && !adding && (
            <div
              data-testid="research-schedule-empty"
              className="border border-dashed border-[var(--border)] px-5 py-8 text-center text-sm text-[var(--ink-muted)]"
            >
              No schedules yet. Add one to have the desk scout stories before
              you get in.
            </div>
          )}

          {schedules.length > 0 && (
            <ul
              data-testid="research-schedule-list"
              className="divide-y divide-[var(--border)] border border-[var(--border)] mb-4"
            >
              {schedules.map((s) => {
                const busy =
                  (updateMut.isPending && updateMut.variables?.id === s.id) ||
                  (deleteMut.isPending && deleteMut.variables === s.id);
                const running =
                  runNowMut.isPending && runNowMut.variables === s.id;
                return (
                  <li
                    key={s.id}
                    data-testid={`research-schedule-row-${s.id}`}
                    className={
                      "flex items-start gap-3 px-4 py-3 " +
                      (s.enabled ? "" : "opacity-60")
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-serif-display text-base leading-snug">
                          {s.name || s.summary}
                        </span>
                        {!s.enabled && (
                          <span className="text-[9px] uppercase tracking-wider font-mono-display px-1.5 py-0.5 bg-neutral-100 text-neutral-600">
                            Paused
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono-display text-[var(--ink-muted)]">
                        <span>{s.summary}</span>
                        {s.enabled && s.next_run_at && (
                          <span>
                            · next {formatInTz(s.next_run_at, tz, "compact")}
                          </span>
                        )}
                        <StatusPill status={s.last_status} />
                      </div>
                      {s.last_status === "error" && s.last_error && (
                        <div className="mt-1 text-[11px] text-red-700 line-clamp-2">
                          {s.last_error}
                        </div>
                      )}
                      {s.guidelines && (
                        <div className="mt-1.5 text-xs text-[var(--ink-muted)] line-clamp-2">
                          {s.guidelines}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => runNowMut.mutate(s.id)}
                      disabled={running}
                      title="Run this schedule now (doesn't affect the next scheduled run)"
                      aria-label={`Run ${s.name || s.summary} now`}
                      data-testid={`research-schedule-run-${s.id}`}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-[var(--border)] hover:border-[var(--slate-900)] text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-50"
                    >
                      {running ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                      Run now
                    </button>

                    <label
                      className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono-display text-[var(--ink-muted)] cursor-pointer select-none"
                      title={s.enabled ? "Pause this schedule" : "Resume this schedule"}
                    >
                      <input
                        type="checkbox"
                        checked={!!s.enabled}
                        disabled={busy}
                        data-testid={`research-schedule-enabled-${s.id}`}
                        onChange={(e) =>
                          updateMut.mutate({
                            id: s.id,
                            patch: { enabled: e.target.checked },
                          })
                        }
                        className="w-3.5 h-3.5 accent-current"
                      />
                      On
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete this schedule?\n\n${s.name || s.summary}\n${s.summary}\n\nPast runs it already produced are kept.`
                          )
                        ) {
                          deleteMut.mutate(s.id);
                        }
                      }}
                      disabled={busy}
                      aria-label={`Delete schedule ${s.name || s.summary}`}
                      data-testid={`research-schedule-delete-${s.id}`}
                      className="shrink-0 inline-flex items-center justify-center w-7 h-7 border border-transparent hover:border-red-200 hover:bg-red-50 text-[var(--ink-muted)] hover:text-red-700 disabled:opacity-50"
                    >
                      {busy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="research-schedule-add"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--border)] text-sm hover:border-[var(--slate-900)]"
            >
              <Plus className="w-4 h-4" />
              Add schedule
            </button>
          )}

          {adding && (
            <div
              data-testid="research-schedule-form"
              className="border border-[var(--border)] px-4 py-4 space-y-4"
            >
              <div>
                <label className="overline block mb-1.5">Name · optional</label>
                <input
                  type="text"
                  value={draft.name}
                  data-testid="research-schedule-name"
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  placeholder="Weekday mornings"
                  className="w-full px-3 py-2 border border-[var(--border)] text-sm outline-none focus:border-[var(--slate-900)]"
                />
              </div>

              <div>
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <label className="overline">Days</label>
                  <button
                    type="button"
                    data-testid="research-schedule-preset-weekdays"
                    onClick={() =>
                      setDraft((d) => ({ ...d, days_of_week: WEEKDAY_PRESET }))
                    }
                    className="text-[11px] font-mono-display text-[var(--ink-muted)] hover:text-[var(--ink)] underline"
                  >
                    Mon–Fri
                  </button>
                  <button
                    type="button"
                    data-testid="research-schedule-preset-weekend"
                    onClick={() =>
                      setDraft((d) => ({ ...d, days_of_week: WEEKEND_PRESET }))
                    }
                    className="text-[11px] font-mono-display text-[var(--ink-muted)] hover:text-[var(--ink)] underline"
                  >
                    Weekends
                  </button>
                </div>
                <DayPicker
                  idPrefix="research-schedule"
                  value={draft.days_of_week}
                  onChange={(days_of_week) =>
                    setDraft((d) => ({ ...d, days_of_week }))
                  }
                />
              </div>

              <div>
                <label className="overline block mb-1.5">
                  Time · {tzAbbreviation(tz)}
                </label>
                <input
                  type="time"
                  value={draft.time_local}
                  data-testid="research-schedule-time"
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, time_local: e.target.value }))
                  }
                  className="px-3 py-2 border border-[var(--border)] text-sm outline-none focus:border-[var(--slate-900)]"
                />
              </div>

              <div>
                <label className="overline block mb-1.5">
                  Guidelines for this schedule · optional
                </label>
                <textarea
                  rows={3}
                  value={draft.guidelines}
                  data-testid="research-schedule-guidelines"
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, guidelines: e.target.value }))
                  }
                  placeholder="Steer these runs without changing your standing editorial brief — e.g. lighter weekend features rather than hard news."
                  className="w-full resize-none px-3 py-2 border border-[var(--border)] text-sm leading-relaxed outline-none focus:border-[var(--slate-900)]"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={submitDraft}
                  disabled={createMut.isPending}
                  data-testid="research-schedule-save"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate-900)] text-white text-sm hover:bg-black disabled:opacity-60"
                >
                  {createMut.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  Save schedule
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAdding(false);
                    setDraft(EMPTY_DRAFT);
                  }}
                  data-testid="research-schedule-cancel"
                  className="px-4 py-2 border border-[var(--border)] text-sm hover:border-[var(--slate-900)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
